import { useState } from 'react'

import { AssistantContextUsage, AssistantEffort } from '@digital-twins/shared-types'

import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Context ring — composer footer indicator for how much of the history
 * ceiling a thread has used.
 *
 * Mounted in composer footers (Home Lab Ask, Threads lobby, and the thread
 * composer after a completed assistant turn). Props stay stable so those
 * surfaces share one signature.
 *
 * Field names are `contextUsage` and `truncated`, confirmed against the
 * python producer and the Nest forwarder, which passes the object through by
 * reference, unrenamed. Older prose elsewhere refers to `contextTruncated`;
 * that field does not exist on the wire, so it is not read here.
 *
 * `truncated` means HISTORY truncation only, i.e. old turns were dropped to
 * fit the ceiling. Evidence/retrieval truncation is computed server-side and
 * never put on the wire, so nothing here may imply anything about dropped
 * research — the copy is scoped to history on purpose.
 */

const RING_SIZE = 16
const RING_STROKE = 2
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

// Neutral below this ratio of historyTokens/ceiling, amber at or above it.
const AMBER_THRESHOLD = 0.7

interface ContextRingProps {
  contextUsage: AssistantContextUsage | null
  truncated: boolean
  effort: AssistantEffort
  onContinueInNewThread: () => void
  className?: string
}

// "12k of 100k", never "12,431 tokens of 100,000": the reader is a
// marketer, not an engineer, and the word "token" is never spelled out.
function formatCompactCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '0'
  }

  if (value >= 1_000_000) {
    const millions = Math.round((value / 1_000_000) * 10) / 10
    return `${Number.isInteger(millions) ? millions : millions.toFixed(1)}m`
  }

  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`
  }

  return String(Math.round(value))
}

function formatEffortLabel(effort: AssistantEffort): string {
  return effort.charAt(0).toUpperCase() + effort.slice(1)
}

// A started conversation is never "0%" of the ceiling: rounding a real but
// tiny history down to zero would read as "nothing has been used yet."
function formatPercent(ratio: number): string {
  const percent = ratio * 100

  if (percent > 0 && percent < 1) {
    return '<1%'
  }

  return `${Math.round(percent)}%`
}

export function ContextRing(props: ContextRingProps) {
  const { contextUsage, truncated, effort, onContinueInNewThread, className } = props

  const [open, setOpen] = useState(false)

  const handleContinue = () => {
    // Close first: `onContinueInNewThread` may navigate away and unmount
    // this component, and a state update belongs before that, not after.
    setOpen(false)
    onContinueInNewThread()
  }

  // A thread before its first completed turn has no usage numbers at all.
  // An empty ring at 0% would read as "this conversation has been measured
  // and found empty," which is a worse signal than showing nothing. Do not
  // "fix" this by rendering a zeroed ring for the null case.
  if (!contextUsage) {
    return null
  }

  const { historyTokens, ceiling, turnRetrievalTokens } = contextUsage

  // `ceiling` is a plain number on the wire, not guaranteed non-zero.
  // Dividing by it would render as a silently broken ring (NaN/Infinity
  // inside the SVG dash array), not a visible error, so a zero (or
  // otherwise non-positive) ceiling flattens to an empty, neutral ring
  // instead of ever reaching a division.
  const ratio = ceiling > 0 ? Math.min(Math.max(historyTokens / ceiling, 0), 1) : 0
  const isAmber = ceiling > 0 && ratio >= AMBER_THRESHOLD

  const filled = ratio * RING_CIRCUMFERENCE
  const gap = RING_CIRCUMFERENCE - filled

  const historyLabel =
    ceiling > 0
      ? `${formatCompactCount(historyTokens)} of ${formatCompactCount(ceiling)}`
      : formatCompactCount(historyTokens)
  const retrievalLabel = formatCompactCount(turnRetrievalTokens)
  const effortLabel = formatEffortLabel(effort)

  // The ring carries no text of its own, so the share of the ceiling lives in
  // the hover tooltip. A zero ceiling has no percentage to state, so it falls
  // back to the raw count rather than inventing one.
  const hoverLabel =
    ceiling > 0 ? `${formatPercent(ratio)} of context used` : `${historyLabel} used`

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={hoverLabel}
                  className={cn(
                    'inline-flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-muted/70',
                    isAmber ? 'text-chart-warning' : 'text-muted-foreground',
                  )}
                  data-testid="thread-context-ring"
                >
                  <svg
                    width={RING_SIZE}
                    height={RING_SIZE}
                    viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                    aria-hidden="true"
                    className="flex-shrink-0"
                  >
                    <circle
                      cx={RING_SIZE / 2}
                      cy={RING_SIZE / 2}
                      r={RING_RADIUS}
                      fill="none"
                      strokeWidth={RING_STROKE}
                      className="stroke-muted-foreground/20"
                    />
                    <circle
                      cx={RING_SIZE / 2}
                      cy={RING_SIZE / 2}
                      r={RING_RADIUS}
                      fill="none"
                      strokeWidth={RING_STROKE}
                      strokeLinecap="round"
                      stroke="currentColor"
                      strokeDasharray={`${filled} ${gap}`}
                      strokeDashoffset={RING_CIRCUMFERENCE / 4}
                    />
                  </svg>
                </button>
              </PopoverTrigger>
            </TooltipTrigger>

            <TooltipContent side="top" data-testid="thread-context-ring-tooltip">
              {hoverLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <PopoverContent align="end" className="w-64" data-testid="thread-context-ring-popover">
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex flex-col gap-1.5">
              <div
                className="flex items-center justify-between gap-2"
                data-testid="thread-context-ring-history"
              >
                <span className="text-muted-foreground">Conversation so far</span>
                <span className="font-medium text-foreground">{historyLabel}</span>
              </div>
              <div
                className="flex items-center justify-between gap-2"
                data-testid="thread-context-ring-retrieval"
              >
                <span className="text-muted-foreground">This turn&apos;s research</span>
                <span className="font-medium text-foreground">{retrievalLabel}</span>
              </div>
            </div>

            <div
              className="flex items-center justify-between gap-2 border-t border-border pt-3"
              data-testid="thread-context-ring-effort"
            >
              <span className="text-muted-foreground">Effort</span>
              <span className="font-medium text-foreground">{effortLabel}</span>
            </div>

            <button
              type="button"
              onClick={handleContinue}
              className="rounded-md bg-muted/70 px-3 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
              data-testid="thread-context-ring-continue"
            >
              Continue in a new thread
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {truncated ? (
        <span className="text-xs text-muted-foreground" data-testid="thread-context-ring-truncated">
          Older messages were trimmed.
        </span>
      ) : null}
    </div>
  )
}
